import type { IndexedStorySection } from '@prezly/theme-kit-core';
import classNames from 'classnames';
import Link from 'next/link';
import type { Hit } from 'react-instantsearch-core';
import { Highlight, Snippet } from 'react-instantsearch-dom';

import { CategoriesList, StoryImage, StoryPublicationDate } from '@/components';
import { useThemeSettings } from '@/hooks';
import { slugifyHeading } from '@/utils';

import { useSearchState } from './SearchStateContextProvider';

import cardStyles from '@/components/StoryCards/StoryCard.module.scss';
import styles from './Hit.module.scss';

interface Props {
    hit: Hit<{ attributes: IndexedStorySection }>;
}

// This is mostly a copy of `StoryCard` component, but since the data structure is a bit different,
// it requires a separate component for indexed content
function HitComponent({ hit }: Props) {
    const { attributes: story } = hit;
    const { categories } = story;
    const { showDate, showSubtitle } = useThemeSettings();
    const { searchState } = useSearchState();
    const sectionHeading = story.section_title ?? story.section_subtitle;
    const sectionHeadingAttribute = story.section_title
        ? 'attributes.section_title'
        : 'attributes.section_subtitle';
    const anchor = sectionHeading ? `#header-${slugifyHeading(sectionHeading)}` : undefined;

    const hasQuery = Boolean(searchState.query?.trim());
    const hasContentSnippet = Boolean(story.content_text);
    const showSectionSnippet = hasQuery && (Boolean(sectionHeading) || hasContentSnippet);

    // strip query params from story links
    const storyLink = {
        pathname: '/[slug]',
        query: { slug: story.slug },
        hash: anchor,
    };

    return (
        <div className={classNames(cardStyles.container, cardStyles.small)}>
            <div className={cardStyles.imageWrapper}>
                <StoryImage
                    story={story}
                    size="default"
                    className={cardStyles.image}
                    placeholderClassName={cardStyles.placeholder}
                />
            </div>
            <div className={cardStyles.content}>
                {categories.length > 0 && (
                    <div className={cardStyles.categories}>
                        <CategoriesList categories={categories} isStatic />
                    </div>
                )}
                <h3 className={classNames(cardStyles.title, cardStyles.titleSmaller)}>
                    <Link
                        href={storyLink}
                        locale={false}
                        className={classNames(cardStyles.titleLink, styles.title)}
                    >
                        <span className={cardStyles.linkMask} />
                        <Highlight hit={hit} attribute="attributes.title" tagName="mark" />
                    </Link>
                </h3>

                {(showSubtitle || showSectionSnippet) && (
                    <p className={cardStyles.subtitle}>
                        {showSectionSnippet ? (
                            <span className={styles.snippet}>
                                {sectionHeading && (
                                    <span className={styles.section}>
                                        <Highlight
                                            hit={hit}
                                            attribute={sectionHeadingAttribute}
                                            tagName="mark"
                                        />
                                    </span>
                                )}
                                {sectionHeading && hasContentSnippet && (
                                    <span className={styles.separator}> · </span>
                                )}
                                {hasContentSnippet && (
                                    <Snippet
                                        hit={hit}
                                        attribute="attributes.content_text"
                                        tagName="mark"
                                    />
                                )}
                            </span>
                        ) : (
                            <span className={cardStyles.titleLink}>{story.subtitle}</span>
                        )}
                    </p>
                )}

                {showDate && (
                    <p className={cardStyles.date}>
                        <StoryPublicationDate story={story} />
                    </p>
                )}
            </div>
        </div>
    );
}

export default HitComponent;
